/**
 * LegendDotView - Small colored dot for chart legends
 *
 * Displays a colored dot indicator used in chart legends.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import React from "react";

import { Box } from "@/components/atoms";

/*
 * Data-driven chart legend colour (with a computed dashed-state alpha) that no
 * semantic token expresses; passed as a single style reference to UNSAFE_style.
 */
const dotStyle = (color: string, isDashed: boolean) => ({
  backgroundColor: isDashed ? `${color}26` : color,
  borderColor: isDashed ? color : undefined,
});

/**
 * Props for LegendDotView component
 */
interface LegendDotViewProps {
  /** Dot color */
  readonly color: string;
  /** Whether the dot has a dashed border */
  readonly isDashed?: boolean;
}

/**
 * LegendDotView displays a small colored dot for chart legends
 * @param props - Component props
 * @param props.color - Dot color
 * @param props.isDashed - Whether the dot has a dashed border
 * @returns Box element styled as a legend dot
 */
const LegendDotView = ({ color, isDashed = false }: LegendDotViewProps) => (
  <Box
    className={`size-2 rounded-full ${isDashed ? "border border-dashed" : ""}`}
    // eslint-disable-next-line no-restricted-syntax -- data-driven legend color with computed dashed-state alpha (${color}26); no semantic token expresses an arbitrary caller color
    UNSAFE_style={dotStyle(color, isDashed)}
  />
);

LegendDotView.displayName = "LegendDotView";

export default React.memo(LegendDotView);
